/* =========================================================================
   LEVEL-UP CELEBRATION (PLAN-progression.md §5.3 — "the ding").

   The one loud moment in the progression loop: rays, the new career level, and
   one warm line in Brother Theo's voice. Deliberately reverent-playful and
   free of doctrinal claims — he's congratulating a pilgrim, not teaching.

   CONTRACT (called by gameController after showXpTally — do not change):
     showLevelUp({ level })

   UNLOCKS: named unlocks ("stakes up to $1,300, Manna Drop unlocked") arrive in
   a later phase. The layout already carries the empty `.lvup-unlock` slot in the
   right place — filling it is then a one-line change here, not a redesign.
   ========================================================================= */
import { esc } from '../engine';
import { icon } from './icons';
import { sndChoir } from '../audio';
import { openMoment } from './momentOverlay';

/** The hard contract with gameController (Agent B). Do not reshape. */
export interface LevelUpOpts {
  level: number;
}

/** Theo's congratulation — one short line, picked deterministically so the same
    level always reads the same way. */
export function theoLevelLine(level: number): string {
  const lines = [
    'The lamp burns a little brighter. Well walked, pilgrim.',
    'Another step up the road, and you barely broke a sweat.',
    "Look at you. I'd offer bread, but you've earned better.",
    'Steady feet, open heart. Onward.',
    "That's the sound of a pilgrim finding their stride.",
  ];
  const n = Math.max(1, Math.round(Number.isFinite(level) ? level : 1));
  return lines[(n - 1) % lines.length];
}

/** Show the level-up moment. Resolves when the player continues. */
export function showLevelUp(opts: LevelUpOpts): Promise<void> {
  const level = Math.max(1, Math.round(Number.isFinite(opts.level) ? opts.level : 1));
  const moment = openMoment({
    id: 'levelUpOverlay',
    cls: 'lvup-ov',
    label: 'Level up',
    guardMs: 500,
    autoMs: 9000, // safety net only — this moment is meant to be tapped away
    html:
      `<div class="mo-card lvup-card">` +
      `<div class="lvup-kicker">Level up</div>` +
      `<div class="lvup-badge"><span class="lvup-badge-lbl">Level</span><span class="lvup-num">${esc(level)}</span></div>` +
      `<div class="lvup-theo"><span class="lvup-theo-mark">${icon('candle', 16)}</span><span class="lvup-theo-line">${esc(theoLevelLine(level))}</span></div>` +
      // Reserved for the unlock line ("Stakes up to $1,300 · Manna Drop unlocked").
      `<div class="lvup-unlock hidden" id="levelUpUnlock"></div>` +
      `<button class="btn lvup-cta" id="levelUpContinue" type="button">Continue</button>` +
      `</div>`,
  });

  moment.root.querySelector<HTMLElement>('#levelUpContinue')?.addEventListener('click', () => moment.close());
  sndChoir(); // no-op until audio has been unlocked by a tap

  return moment.done;
}
